import type {
  LeadCardCheckboxItemData,
  LeadCardHistoryItemData,
} from '@app/components/LeadCard/data';
import { normalizeId } from '@app/components/LeadCard/model/filterStorage';

export interface LeadHistoryFilterState {
  sourceItems: LeadCardCheckboxItemData[];
  actionItems: LeadCardCheckboxItemData[];
}

// Custom action ids injected alongside backend action ids in toActionItems.
const CUSTOM_ACTION_SYSTEM = 'system';
const CUSTOM_ACTION_TASKS = 'tasks';
const CUSTOM_ACTION_LEADS = 'leads';

const SYSTEM_ACTION_USER_ID = 1;
const OBJECT_TYPE_CRM_TASK = 'CrmTask';
const OBJECT_TYPE_LEAD = 'Lead';

const toIdSet = (items: LeadCardCheckboxItemData[]): Set<string> =>
  new Set(items.map(({ id }) => id));

const toCheckedIdSet = (items: LeadCardCheckboxItemData[]): Set<string> =>
  new Set(items.filter(({ checked }) => checked).map(({ id }) => id));

// Applies the Lead Card source/action checkbox state to history rows.
// Mirrors the old chat filteredHistoryData rules: a row is hidden only when its
// source/action is a known option that is currently unchecked, plus the custom
// system/tasks/leads toggles. Unknown sources/actions always pass through.
export const filterLeadHistoryItems = (
  items: LeadCardHistoryItemData[],
  { sourceItems, actionItems }: LeadHistoryFilterState
): LeadCardHistoryItemData[] => {
  if (sourceItems.length === 0 && actionItems.length === 0) return items;

  const availableSourceIds = toIdSet(sourceItems);
  const selectedSourceIds = toCheckedIdSet(sourceItems);
  const availableActionIds = toIdSet(actionItems);
  const selectedActionIds = toCheckedIdSet(actionItems);

  const allowSystem = selectedActionIds.has(CUSTOM_ACTION_SYSTEM);
  const allowTasks = selectedActionIds.has(CUSTOM_ACTION_TASKS);
  const allowLeads = selectedActionIds.has(CUSTOM_ACTION_LEADS);

  return items.filter((item) => {
    const sourceId = normalizeId(item.ChatMessages?.[0]?.SocialType);
    if (
      sourceId !== null &&
      availableSourceIds.has(sourceId) &&
      !selectedSourceIds.has(sourceId)
    ) {
      return false;
    }

    // System rows (created by the system user) are governed solely by the
    // `Системные` toggle. A system row often also carries an ObjectType such
    // as `Lead` or `CrmTask` (e.g. a lead created by the system), so the
    // task/lead/action gates below must not hide it: selecting only
    // `Системные` must still show these rows.
    if (
      item.ActionUserId === SYSTEM_ACTION_USER_ID &&
      !item.ChatMessages?.[0]?.RecommendChat
    ) {
      return allowSystem;
    }

    const actionId = normalizeId(item.ActionId);
    if (
      actionId !== null &&
      availableActionIds.has(actionId) &&
      !selectedActionIds.has(actionId)
    ) {
      return false;
    }

    if (!allowTasks && item.ObjectType === OBJECT_TYPE_CRM_TASK) {
      return false;
    }

    if (!allowLeads && item.ObjectType === OBJECT_TYPE_LEAD) {
      return false;
    }

    return true;
  });
};
